/** Default maximum utterance length (characters) — long sentences are
 * redistributed into smaller chunks so no single `SpeechSynthesisUtterance`
 * runs long enough to make the seek bar's per-segment granularity feel
 * coarse. */
const MAX_UTTERANCE_LENGTH = 160

interface SegmentLike {
  segment: string
}

interface SegmenterLike {
  segment(text: string): Iterable<SegmentLike>
}

interface SegmenterCtor {
  new (locale: string, options: { granularity: 'sentence' }): SegmenterLike
}

/** `Intl.Segmenter` isn't declared by TypeScript's `lib.es*.d.ts` at this
 * project's configured `lib` target, so this is a type guard rather than a
 * cast — it narrows `typeof Intl` only when the constructor is actually
 * present (it is, in every browser this app supports; this only matters for
 * the jsdom/Node test environment and very old browsers). */
function hasSegmenter(intl: typeof Intl): intl is typeof Intl & { Segmenter: SegmenterCtor } {
  return 'Segmenter' in intl
}

/** Splits `text` into sentences. Prefers `Intl.Segmenter`'s `'sentence'`
 * granularity (locale-aware sentence boundaries); falls back to a simple
 * punctuation-based split for environments where it's unavailable (jsdom in
 * some Node versions, some older engines). */
function segmentSentences(text: string, lang: string): string[] {
  const intl = Intl
  if (hasSegmenter(intl)) {
    const segmenter = new intl.Segmenter(lang, { granularity: 'sentence' })
    return Array.from(segmenter.segment(text), (entry) => entry.segment.trim()).filter(
      (sentence) => sentence.length > 0,
    )
  }

  return text
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter((sentence) => sentence.length > 0)
}

/** Redistributes one sentence longer than `maxChars` into several chunks,
 * preferring to cut on `;`, `:` or `,` (in that order) and falling back to
 * the last space in the window, so a chunk never ends mid-word. */
function splitLongSentence(sentence: string, maxChars: number): string[] {
  if (sentence.length <= maxChars) return [sentence]

  const parts: string[] = []
  let remaining = sentence

  while (remaining.length > maxChars) {
    const window = remaining.slice(0, maxChars + 1)
    let cutIndex = Math.max(
      window.lastIndexOf(';'),
      window.lastIndexOf(':'),
      window.lastIndexOf(','),
    )
    if (cutIndex === -1) cutIndex = window.lastIndexOf(' ')
    if (cutIndex <= 0) cutIndex = maxChars - 1

    parts.push(remaining.slice(0, cutIndex + 1).trim())
    remaining = remaining.slice(cutIndex + 1).trim()
  }

  if (remaining) parts.push(remaining)
  return parts
}

/**
 * Turns one block of plain text into the sentence-sized utterances the
 * read-aloud player speaks one at a time: sentence boundaries come from
 * `Intl.Segmenter`, and any sentence longer than `maxChars` (default 160) is
 * further redistributed by `splitLongSentence`.
 */
export function splitIntoUtterances(
  text: string,
  lang: string,
  maxChars: number = MAX_UTTERANCE_LENGTH,
): string[] {
  const trimmed = text.trim()
  if (!trimmed) return []

  const sentences = segmentSentences(trimmed, lang)
  const utterances: string[] = []
  for (const sentence of sentences) {
    utterances.push(...splitLongSentence(sentence, maxChars))
  }
  return utterances.filter((utterance) => utterance.length > 0)
}
